import React, { useEffect } from 'react';
import { arrayMove, insertAtIndex } from '#lib/utils/array.js';
import { set } from 'lodash-es';
import { group, ungroup } from '../helpers.js';
import { newId } from '#helpers/helpers.js';

const ACTIONS = {
    REPLACE_STATE: 'replace_state',
    DELETE_LOCATION: 'delete_location',
    ADD_PLACE: 'add_place',
    DELETE_PLACE: 'delete_place',
    MOVE_PLACE: 'move_place',
    EDIT_PLACE: 'edit_place',
    MOVE_GALLERY: 'move_gallery',
    EDIT_HOURS: 'edit_hours',
    ADD_PERIOD: 'add_period',
    DELETE_PERIOD: 'delete_period',
    SELECT_ALL_PLACES: 'select_all_places',
    UPDATE_ALL: 'update_all',
    UPDATE_STATE_FROM_NOTIFICATION: 'update_state_from_notification'
};

const makeCustomPlace = ({ id, filterId, name, type }) => ({
    id: id ?? newId(),
    filterId: filterId,
    filterName: 'Other',
    name: name,
    type: type
});

const makeEmptyLocation = (name) => ({
    name: name,
    locked: false,
    info: [],
    suggestions: []
});

function reducer(state, action) {
    switch (action.type) {
        case ACTIONS.REPLACE_STATE: {
            return [...action.payload];
        }
        case ACTIONS.DELETE_LOCATION: {
            const { name } = action.payload;
            const index = state.findIndex((place) => place.name === name);
            return state.filter((_, i) => i !== index);
        }
        case ACTIONS.ADD_LOCATION: {
            const { name } = action.payload;
            // TODO: se creo place con stesso nome lo vieto?
            // nel caso i name possono essere usati come id
            const placeExists = state.some((place) => place.name === name);
            !placeExists && state.push(makeEmptyLocation(name));
            return [...state];
        }
        case ACTIONS.ADD_PLACE: {
            const { name, type, id } = action.payload;
            const updated = structuredClone(state);
            // Se c'è type crea una info o suggestion per la location
            if (type) {
                const place = updated.find((place) => place.name === name);
                const newPlaces = insertAtIndex(
                    place[type],
                    place[type].length,
                    makeCustomPlace({ filterId: 19, name: 'Custom', type: type, id })
                );
                set(place, type, newPlaces);
            }
            return updated;
        }
        case ACTIONS.SELECT_ALL_PLACES: {
            const { location: name, value } = action.payload;
            const updated = structuredClone(state);
            const location = updated.find((place) => place.name === name);
            if (location) {
                const infos = location.info;
                const sugg = location.suggestions;
                infos.concat(sugg).forEach((el) => (el.checked = value));
            }
            return updated;
        }
        case ACTIONS.DELETE_PLACE: {
            const { name, type, id } = action.payload;
            const updated = structuredClone(state);
            const place = updated.find((place) => place.name === name);
            const filteredPlace = place[type]?.filter((res) => res.id !== id);
            set(place, type, filteredPlace);
            return updated;
        }
        case ACTIONS.MOVE_PLACE: {
            const { name, type, from, to } = action.payload;
            const updated = structuredClone(state);
            const place = updated.find((place) => place.name === name);
            place[type] = arrayMove(place[type], from, to);
            return updated;
        }
        case ACTIONS.EDIT_PLACE: {
            const { name, type, field, data, id } = action.payload;
            const updated = structuredClone(state);
            let result = updated.find((place) => place.name === name); // result è la location
            // se type e id valorizzati la modifica è su un place (info o suggestion), altrimenti è sulla location
            if (type && id) result = result[type].find((res) => res.id === id);
            set(result, field, data);
            return updated;
        }
        case ACTIONS.MOVE_GALLERY: {
            const { name, type, index, id } = action.payload;
            const updated = structuredClone(state);
            let result = updated.find((place) => place.name === name); // result è la location
            if (type && id) result = result[type].find((res) => res.id === id);
            const gallery = result.gallery;
            const [item] = gallery.splice(index, 1);
            gallery.unshift(item);
            return updated;
        }
        case ACTIONS.EDIT_HOURS: {
            const { name, type, field, id, dayIndex, period, typeHours, data } = action.payload;
            const updated = structuredClone(state);
            const place = updated.find((place) => place.name === name);
            const suggestion = place[type].find((res) => res.id === id);
            const hours = suggestion[field].at(dayIndex).periods.at(period);
            set(hours, typeHours, data);
            return updated;
        }
        case ACTIONS.ADD_PERIOD: {
            const { name, type, field, id, dayIndex } = action.payload;
            const updated = structuredClone(state);
            const place = updated.find((place) => place.name === name);
            const suggestion = place[type].find((res) => res.id === id);
            const day = suggestion[field].at(dayIndex);
            day.periods.push({ open: '', close: '' });
            return updated;
        }
        case ACTIONS.DELETE_PERIOD: {
            const { name, type, field, id, dayIndex, periodIndex } = action.payload;
            const updated = structuredClone(state);
            const place = updated.find((place) => place.name === name);
            const suggestion = place[type].find((res) => res.id === id);
            const day = suggestion[field].at(dayIndex);
            const newPeriods = day.periods.filter((_, idx) => idx !== periodIndex);
            set(day, 'periods', newPeriods);
            return updated;
        }
        case ACTIONS.UPDATE_ALL: {
            const { newState } = action.payload;
            let updated = structuredClone(state);
            updated = newState;
            return updated;
        }
        case ACTIONS.UPDATE_STATE_FROM_NOTIFICATION: {
            const newPlaces = action.payload;
            const groupedPlaces = group(newPlaces);
            return groupedPlaces;
        }
    }
}

export function usePlaces(initialState, init, setUnsavedChanges) {
    const [state, dispatch] = React.useReducer(reducer, initialState, init);

    useEffect(() => {
        if (initialState) {
            const results = init(initialState);
            dispatch({ type: ACTIONS.REPLACE_STATE, payload: results });
        }
    }, [initialState]);

    const deleteLocation = React.useCallback((name) => {
        dispatch({
            type: ACTIONS.DELETE_LOCATION,
            payload: { name }
        });
        setUnsavedChanges(true);
    }, []);

    const addLocation = React.useCallback((name) => {
        dispatch({
            type: ACTIONS.ADD_LOCATION,
            payload: { name }
        });
        setUnsavedChanges(true);
    }, []);

    const addPlace = React.useCallback((name, type, id) => {
        dispatch({
            type: ACTIONS.ADD_PLACE,
            payload: { name, type, id }
        });
        setUnsavedChanges(true);
    }, []);

    const deletePlace = React.useCallback((name, type, id) => {
        dispatch({
            type: ACTIONS.DELETE_PLACE,
            payload: { name, type, id }
        });
        setUnsavedChanges(true);
    }, []);

    const movePlace = React.useCallback((name, type, from, to) => {
        dispatch({ type: ACTIONS.MOVE_PLACE, payload: { name, type, from, to } });
        setUnsavedChanges(true);
    }, []);

    const editPlace = React.useCallback((name, type, field, data, id, multiAction) => {
        if (multiAction) {
            for (const key in data) {
                dispatch({
                    type: ACTIONS.EDIT_PLACE,
                    payload: { name, type, field: key, data: data[key], id }
                });
            }
        } else {
            dispatch({
                type: ACTIONS.EDIT_PLACE,
                payload: { name, type, field, data, id }
            });
        }
        setUnsavedChanges(true);
    }, []);

    const moveGallery = React.useCallback((name, type, index, id) => {
        dispatch({
            type: ACTIONS.MOVE_GALLERY,
            payload: { name, type, index, id }
        });
        setUnsavedChanges(true);
    }, []);

    const editHours = React.useCallback((name, type, field, id, dayIndex, period, typeHours, data) => {
        dispatch({
            type: ACTIONS.EDIT_HOURS,
            payload: { name, type, field, id, dayIndex, period, typeHours, data }
        });
        setUnsavedChanges(true);
    }, []);

    // usato al posto di editPlace per lockare un place senza settare il flag unsavedChanges
    const lockPlace = React.useCallback((name, type, field, value, id) => {
        dispatch({
            type: ACTIONS.EDIT_PLACE,
            payload: { name, type, field, data: value, id }
        });
    }, []);

    const addPeriod = React.useCallback((name, type, field, id, dayIndex) => {
        dispatch({
            type: ACTIONS.ADD_PERIOD,
            payload: { name, type, field, id, dayIndex }
        });
        setUnsavedChanges(true);
    }, []);

    const deletePeriod = React.useCallback((name, type, field, id, dayIndex, periodIndex) => {
        dispatch({
            type: ACTIONS.DELETE_PERIOD,
            payload: { name, type, field, id, dayIndex, periodIndex }
        });
        setUnsavedChanges(true);
    }, []);

    const updateAll = React.useCallback((newState) => {
        dispatch({
            type: ACTIONS.UPDATE_ALL,
            payload: { newState }
        });
        setUnsavedChanges(true);
    }, []);

    const updateStateWithFromNotification = React.useCallback((newState) => {
        dispatch({ type: ACTIONS.UPDATE_STATE_FROM_NOTIFICATION, payload: newState });
    }, []);

    const selectAllPlaces = React.useCallback((location, value) => {
        dispatch({
            type: ACTIONS.SELECT_ALL_PLACES,
            payload: { location, value }
        });
    }, []);

    return {
        places: state,
        deleteLocation,
        addLocation,
        addPlace,
        deletePlace,
        lockPlace,
        movePlace,
        editPlace,
        moveGallery,
        editHours,
        addPeriod,
        deletePeriod,
        selectAllPlaces,
        updateAll,
        updateStateWithFromNotification
    };
}
